import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

import type { Beer } from 'utils/types/Beer';
import { BeerService } from 'services/firebase/beerService';
import { useCart } from 'services/cart/CartContext';
import { analyticsService } from 'services/analytics/analyticsService';
import { SemAlcool, SemGluten, LowCarb } from 'assets/icons';
import styles from './styles';

const BeerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart, openCart } = useCart();

  const [beer, setBeer] = useState<Beer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const loadBeer = async () => {
      if (!id) {
        setError('ID da cerveja não encontrado');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const beerData = await BeerService.getBeerById(id);

        if (!beerData) {
          setError('Cerveja não encontrada');
        } else {
          setBeer(beerData);

          // Track beer detail view
          analyticsService.trackBeerDetailView(
            beerData.id,
            beerData.name,
            beerData.price,
            beerData.style,
          );
        }
      } catch (err) {
        console.error('Erro ao carregar cerveja:', err);
        setError('Erro ao carregar detalhes da cerveja');
      } finally {
        setLoading(false);
      }
    };

    loadBeer();
  }, [id]);

  const handleAddToCart = () => {
    if (beer && beer.inStock) {
      addToCart(beer, quantity);
      openCart();

      // Track add to cart event
      analyticsService.trackAddToCart(beer.id, beer.name, beer.price, quantity);
    }
  };

  const handleQuantityChange = (change: number) => {
    setQuantity(prev => Math.max(1, prev + change));
  };

  if (loading) {
    return (
      <Container sx={styles.container}>
        <Box sx={styles.loadingContainer}>
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Carregando detalhes da cerveja...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !beer) {
    return (
      <Container sx={styles.container}>
        <Alert severity="error" sx={styles.errorAlert}>
          {error || 'Cerveja não encontrada'}
        </Alert>
        <Button
          variant="outlined"
          onClick={() => navigate('/beer-store')}
          sx={{ mt: 2 }}
        >
          Voltar para a loja
        </Button>
      </Container>
    );
  }

  const discountPercentage = beer.originalPrice
    ? Math.round(((beer.originalPrice - beer.price) / beer.originalPrice) * 100)
    : 0;

  return (
    <Container sx={styles.container}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator="›"
        aria-label="breadcrumb"
        sx={styles.breadcrumbs}
      >
        <Link
          underline="hover"
          sx={styles.breadcrumbLink}
          onClick={() => navigate('/')}
        >
          Home
        </Link>
        <Link
          underline="hover"
          sx={styles.breadcrumbLink}
          onClick={() => navigate('/beer-store')}
        >
          Loja
        </Link>
        <Typography sx={styles.breadcrumbCurrent}>{beer.name}</Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Imagem da cerveja */}
        <Grid item xs={12} md={6}>
          <Box sx={styles.imageWrapper}>
            {(beer.semAlcool || beer.semGluten) && (
              <Box sx={styles.stampsBadge}>
                {beer.semAlcool && (
                  <Box
                    component="img"
                    src={SemAlcool}
                    alt="Sem álcool"
                    sx={styles.stampIcon}
                  />
                )}
                {beer.semGluten && (
                  <Box
                    component="img"
                    src={SemGluten}
                    alt="Sem glúten"
                    sx={styles.stampIcon}
                  />
                )}
              </Box>
            )}
            {beer.lowCarb && (
              <Box sx={styles.stampsBadgeRight}>
                <Box
                  component="img"
                  src={LowCarb}
                  alt="Low Carb"
                  sx={styles.stampIcon}
                />
              </Box>
            )}
            <Box sx={styles.imageContainer}>
              <img src={beer.image} alt={beer.name} style={styles.productImage} />
            </Box>
          </Box>
        </Grid>

        {/* Card com preços e ações */}
        <Grid item xs={12} md={6}>
          <Card sx={styles.priceCard}>
            <CardContent sx={styles.cardContent}>
              <Typography variant="h4" sx={styles.productName}>
                {beer.name}
              </Typography>

              <Typography variant="h6" sx={styles.brewery}>
                {beer.brewery}
              </Typography>

              <Typography variant="body1" sx={styles.productInfo}>
                {beer.style} • {beer.abv}% ABV • {beer.volume}ml
              </Typography>

              {/* Seção de preços regulares */}
              <Box sx={styles.priceSection}>
                {beer.originalPrice && (
                  <Box sx={styles.discountBadge}>
                    <LocalOfferIcon sx={styles.discountIcon} />
                    <Typography variant="caption" sx={styles.discountText}>
                      -{discountPercentage}%
                    </Typography>
                  </Box>
                )}

                {beer.originalPrice && (
                  <Typography variant="h6" sx={styles.originalPrice}>
                    R$ {beer.originalPrice.toFixed(2)}
                  </Typography>
                )}

                <Typography variant="h4" sx={styles.currentPrice}>
                  R$ {beer.price.toFixed(2)}
                </Typography>
              </Box>

              {/* Seção de preço para assinantes */}
              <Box sx={styles.subscriberSection}>
                <Typography variant="body2" sx={styles.subscriberLabel}>
                  PREÇO ASSINANTE:
                </Typography>
                <Typography variant="h4" sx={styles.subscriberPrice}>
                  R$ {beer.subscriberPrice.toFixed(2)}
                </Typography>
                <Typography variant="caption" sx={styles.savingsText}>
                  🔥 ASSINE E ECONOMIZE R${' '}
                  {(beer.price - beer.subscriberPrice).toFixed(2)} NESSA COMPRA!
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => navigate('/')}
                  sx={styles.subscribeButton}
                >
                  ASSINE AGORA
                </Button>
              </Box>

              {/* Controle de quantidade */}
              <Box sx={styles.quantitySection}>
                <Typography variant="body1" sx={styles.quantityLabel}>
                  Quantidade:
                </Typography>
                <Box sx={styles.quantityControls}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                    sx={styles.quantityButton}
                  >
                    -
                  </Button>
                  <Typography variant="h6" sx={styles.quantityValue}>
                    {quantity}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleQuantityChange(1)}
                    sx={styles.quantityButton}
                  >
                    +
                  </Button>
                </Box>
              </Box>

              {/* Botão adicionar ao carrinho */}
              <Button
                variant="contained"
                size="large"
                fullWidth
                disabled={!beer.inStock}
                startIcon={<AddShoppingCartIcon />}
                onClick={handleAddToCart}
                sx={styles.addToCartButton}
              >
                {beer.inStock ? 'ADICIONAR AO CARRINHO' : 'INDISPONÍVEL'}
              </Button>

              {!beer.inStock && (
                <Typography variant="caption" sx={styles.outOfStockText}>
                  Este produto está temporariamente fora de estoque
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Seção: Saiba mais sobre este produto */}
      <Box sx={styles.descriptionSection}>
        <Typography variant="h5" sx={styles.sectionTitle}>
          SAIBA MAIS SOBRE ESTE PRODUTO
        </Typography>

        <Typography variant="body1" sx={styles.description}>
          {beer.description}
        </Typography>

        {/* Informações técnicas */}
        <Grid container spacing={2} sx={styles.technicalInfo}>
          <Grid item xs={6} sm={3}>
            <Typography variant="body2" sx={styles.infoLabel}>
              Teor Alcoólico
            </Typography>
            <Typography variant="h6" sx={styles.infoValue}>
              {beer.abv}% ABV
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="body2" sx={styles.infoLabel}>
              Volume
            </Typography>
            <Typography variant="h6" sx={styles.infoValue}>
              {beer.volume}ml
            </Typography>
          </Grid>

          <Grid item xs={6} sm={3}>
            <Typography variant="body2" sx={styles.infoLabel}>
              Estilo
            </Typography>
            <Typography variant="h6" sx={styles.infoValue}>
              {beer.style}
            </Typography>
          </Grid>

          {beer.ibu && (
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" sx={styles.infoLabel}>
                Amargor (IBU)
              </Typography>
              <Typography variant="h6" sx={styles.infoValue}>
                {beer.ibu}
              </Typography>
            </Grid>
          )}

          {beer.calories != null && (
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" sx={styles.infoLabel}>
                Calorias
              </Typography>
              <Typography variant="h6" sx={styles.infoValue}>
                {beer.calories} kcal/100ml
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Container>
  );
};

export default BeerDetail;
